import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaStore,
  FaChartLine,
  FaWallet,
  FaHandHoldingUsd,
  FaUser,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaExchangeAlt,
  FaShieldAlt,
} from "react-icons/fa";
import {
  MdDashboard,
} from "react-icons/md";
import useAuthStore from "../../store/useAuthStore";
import useAdminStore from "../../store/useAdminStore";
import "./Header.css";
import API_CONFIG from "../../config/api";

const Header = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cryptoData, setCryptoData] = useState({
    btc: { price: 0, change24h: 0, loading: true },
    eth: { price: 0, change24h: 0, loading: true },
    usdt: { price: 1, change24h: 0, loading: false },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuthStore();
  const { isAdmin, checkAdminStatus } = useAdminStore();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL = `${API_CONFIG.BASE_URL}/v1/homepage-external-price/`;

  // Fetch crypto data from API
  const fetchCryptoData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Transform API data for the ticker
      const transformedData = {
        btc: {
          price: result.pairs_data?.["BTC-USDT"]?.price || 0,
          change24h: result.pairs_data?.["BTC-USDT"]?.price_24h_value || 0,
          loading: false,
        },
        eth: {
          price: result.pairs_data?.["ETH-USDT"]?.price || 0,
          change24h: result.pairs_data?.["ETH-USDT"]?.price_24h_value || 0,
          loading: false,
        },
        usdt: {
          price: result.pairs_data?.["USDC-USDT"]?.price || 1,
          change24h: 0,
          loading: false,
        },
      };

      setCryptoData(transformedData);
    } catch (err) {
      console.error("Failed to fetch crypto data:", err);
      setError(err.message || "Failed to fetch market data");

      // Set default values on error
      setCryptoData({
        btc: { price: 0, change24h: 0, loading: false },
        eth: { price: 0, change24h: 0, loading: false },
        usdt: { price: 1, change24h: 0, loading: false },
      });
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch data on component mount and set up interval
  useEffect(() => {
    fetchCryptoData();

    // Refresh data every 30 seconds
    const interval = setInterval(fetchCryptoData, 30000);

    return () => clearInterval(interval);
  }, [fetchCryptoData]);

  // Check admin status when user changes
  useEffect(() => {
    if (isAuthenticated && user?.username) {
      checkAdminStatus(user.username);
    }
  }, [isAuthenticated, user?.username, checkAdminStatus]);

  // Handle dropdown click toggle
  const handleDropdownClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle mobile menu item click
  const handleMobileMenuItemClick = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsMobileMenuOpen(false);
    // You can add a custom notification here if needed
  };

  const navItems = [
    // {
    //   key: "/swap",
    //   icon: FaExchangeAlt,
    //   label: "Trade BTC1",
    //   protected: false,
    // },
    {
      key: "/markets",
      icon: FaStore,
      label: "Markets",
      protected: false,
    },
    {
      key: "/wallet",
      icon: FaWallet,
      label: "Wallet",
      protected: true,
    },
    {
      key: "/trade",
      icon: FaChartLine,
      label: "Digital Asset Marketplace",
      protected: false,
    },

    {
      key: "/p2p",
      icon: FaHandHoldingUsd,
      label: "P2P",
      protected: true,
    },
  ];

  const userMenuItems = [
    {
      key: "profile",
      icon: FaUser,
      label: "Profile",
      onClick: () => navigate("/profile"),
    },
    ...(isAdmin
      ? [
          {
            key: "admin",
            icon: MdDashboard,
            label: "Admin Panel",
            onClick: () => navigate("/admin"),
          },
        ]
      : []),
    {
      key: "logout",
      icon: FaSignOutAlt,
      label: "Sign out",
      onClick: handleLogout,
      danger: true,
    },
  ];

  const renderUserSection = () => {
    if (isAuthenticated) {
      return (
        <div className="user-section">
          <div className="user-dropdown" ref={dropdownRef}>
            <button
              className="user-avatar-container !px-4"
              onClick={handleDropdownClick}
            >
            
              <div className="user-info">
                <span className="user-name">
                  {user?.first_name || user?.username?.split("@")[0] || "User"}
                </span>
                <span className="user-tag">Premium</span>
              </div>
              <div className="dropdown-icon">
                <span
                  className={`dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
                >
                  ▼
                </span>
              </div>
            </button>

            <div className={`dropdown-menu ${isDropdownOpen ? "show" : ""}`}>
              {userMenuItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.key}
                    className={`dropdown-item ${item.danger ? "danger" : ""}`}
                    onClick={item.onClick}
                  >
                    <span className="item-icon">
                      <IconComponent />
                    </span>
                    <span className="item-label">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-buttons">
        <button className="btn btn-text" onClick={() => navigate("/login")}>
          Sign in
        </button>
        <button
          className="btn btn-text"
          onClick={() => navigate("/register")}
        >
          Get Started
        </button>
      </div>
    );
  };

  const renderMobileMenu = () => (
    <div className={`mobile-menu-overlay ${isMobileMenuOpen ? "show" : ""}`}>
      <div className="mobile-menu" ref={mobileMenuRef}>
        <div className="mobile-menu-header">
          {isAuthenticated ? (
            <div className="mobile-user-info">
              <div className="mobile-user-avatar">
                {(
                  user?.first_name?.[0] ||
                  user?.username?.[0] ||
                  "U"
                ).toUpperCase()}
              </div>
              <div className="mobile-user-details">
                <span className="mobile-user-name">
                  {user?.first_name || user?.username?.split("@")[0] || "User"}
                </span>
                <span className="mobile-user-tag">Premium</span>
              </div>
            </div>
          ) : (
            <div className="mobile-brand-info">
              <h2 className="mobile-brand-title">P2P1</h2>
            </div>
          )}
          <button
            className="mobile-menu-close"
            onClick={handleMobileMenuToggle}
          >
            <FaTimes />
          </button>
        </div>

        <div className="mobile-menu-content">
          <nav className="mobile-nav">
            {navItems
              .filter((item) => isAuthenticated || !item.protected)
              .map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.key;
                return (
                  <button
                    key={item.key}
                    className={`mobile-menu-item ${isActive ? "active" : ""}`}
                    onClick={() => handleMobileMenuItemClick(item.key)}
                  >
                    <span className="mobile-menu-icon">
                      <IconComponent />
                    </span>
                    <span className="mobile-menu-label">{item.label}</span>
                  </button>
                );
              })}
          </nav>

          {isAuthenticated && (
            <>
              <div className="mobile-menu-divider"></div>

              <div className="mobile-user-actions">
                {userMenuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={item.key}
                      className={`mobile-menu-item ${
                        item.danger ? "danger" : ""
                      }`}
                      onClick={item.onClick}
                    >
                      <span className="mobile-menu-icon">
                        <IconComponent />
                      </span>
                      <span className="mobile-menu-label">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header className={`app-header !border-b !border-[#2A356D] ${!isAuthenticated ? "no-nav" : ""}`}>
        <div className="header-container">
          {/* Logo and Brand */}
          <div className="header-leftnav">
            <Link to="/" className="brand-link">
              <div className="brand-logo">
                <img src="/p2p1-logo.svg" alt="P2P1" className="logo-image" />
                <p className="text-white text-3xl font-semibold pl-2">P2P1</p>
              </div>
             
            </Link>
          </div>

          {/* Navigation Menu */}
          <div className="header-center !items-start !justify-start">
            <nav className="header-menu">
              {navItems
                .filter((item) => isAuthenticated || !item.protected)
                .map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.key;
                  return (
                    <Link
                      key={item.key}
                      to={item.key}
                      className={`menu-item !rounded-full ${isActive ? "active" : ""}`}
                    >
                     
                      <span className="menu-label">{item.label}</span>
                    </Link>
                  );
                })}
            </nav>
          </div>

          {/* Right Side Actions */}
          <div className="header-rightnav">
            {/* User Section */}
            {renderUserSection()}

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-button"
              onClick={handleMobileMenuToggle}
            >
              <FaBars />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {renderMobileMenu()}
    </>
  );
};

export default Header;
